import getJwtToken from 'utils/GetJwtToken';
import { resolveTrackedRoute } from './routePatterns';
import { sendTrackBatch, warmCsrfToken } from './transport';
import { Tracker } from './tracker';
import type { KeyValueStore, SessionEndReason } from './types';

const uuid = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const tabStorage = (): KeyValueStore | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isSignedIn = (): boolean => Boolean(getJwtToken());

let tracker: Tracker | null = null;

export function getTracker(): Tracker {
  if (!tracker) {
    tracker = new Tracker({
      uuid,
      storage: tabStorage(),
      isVisible: () => document.visibilityState === 'visible',
      isAuthenticated: isSignedIn,
      matchRoute: resolveTrackedRoute,
      send: sendTrackBatch,
    });
  }
  return tracker;
}

/** Reports the current page (and, on the shared /success page, the outcome kind). Fetches the CSRF token early for signed-in users. */
export function trackNavigation(pathname: string, kind?: unknown): void {
  getTracker().onNavigate(pathname, kind);
  if (isSignedIn()) warmCsrfToken();
}

/** Ends the tracking session (call BEFORE the redirect or the token clearing that signs the user out). */
export function trackSessionEnd(reason: SessionEndReason, waitMs = 0): Promise<void> {
  try {
    return getTracker().endSession(reason, waitMs);
  } catch {
    return Promise.resolve();
  }
}

/** visibilitychange / pagehide / pageshow, wired to the tracker. Returns the cleanup. */
export function attachBrowserListeners(): () => void {
  const onVisibility = (): void =>
    getTracker().onVisibilityChange(document.visibilityState === 'visible');
  const onPageHide = (): void => getTracker().onVisibilityChange(false);
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) getTracker().onVisibilityChange(document.visibilityState === 'visible');
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
  };
}
